import { useContext, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateUserDetails } from "../../../../../redux/features/authSlice";
import { AiLabEnrolmentContext } from "../../ai-lab/AiLab";
import { AI_LAB_COURSES } from "../../../constants/ai-lab-constants";
import axios from "axios";
import envConfig from "../../../../../config/envConfig";
import {
  collection,
  doc,
  Timestamp,
  writeBatch,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../../../../../config/firebaseConfig";

export const useEnrolmentCheckout = ({ program }) => {
  const dispatch = useDispatch();
  const {
    childrenData,
    setChildrenData,
    otherQueries,
    totalAmount,
    enrolmentId,
    setEnrolmentId,
    setPaymentSuccess,
    setPaymentFailed,
    loading,
    setLoading,
    error,
    setError,
    stage,
    setStage,
    parentsDetails,
    parentId,
  } = useContext(AiLabEnrolmentContext);

  // Local state for pre-filling parent details
  const [parentContact, setParentContact] = useState("");
  const [parentEmail, setParentEmail] = useState("");

  useEffect(() => {
    const fetchParentDetailsDirectly = async () => {
      const email = auth?.currentUser?.email || parentsDetails?.email || "";
      let phone = parentsDetails?.phone || "";

      if (!phone && auth?.currentUser?.uid) {
        try {
          const q = query(
            collection(db, "parents"),
            where("uid", "==", auth.currentUser.uid),
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docData = snap.docs[0].data();
            phone = docData.phone || "";
          }
        } catch (err) {
          console.error("Error fetching parent phone directly:", err);
        }
      }

      setParentContact(phone);
      setParentEmail(email);
    };

    fetchParentDetailsDirectly();
  }, [parentsDetails, auth?.currentUser]);

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.body.appendChild(script);
    });
  };

  // Perform client-side validations
  const validateForm = () => {
    for (let i = 0; i < childrenData.length; i++) {
      const child = childrenData[i];
      if (!child.name?.trim()) {
        setError(`Please enter a name for Child ${i + 1}`);
        return false;
      }
      if (!child.grade) {
        setError(`Please select a class/grade for Child ${i + 1}`);
        return false;
      }
      if (!child.interests || child.interests.length === 0) {
        setError(`Please select at least one course for ${child.name}`);
        return false;
      }

      // Check schedules & overlaps
      const scheduledSlots = new Set();
      for (let j = 0; j < child.interests.length; j++) {
        const courseId = child.interests[j];
        const sched = child.schedule?.[courseId];
        if (!sched?.day || !sched?.timeSlot) {
          setError(
            `Please select a schedule slot for ${child.name}'s ${AI_LAB_COURSES.find((c) => c.id === courseId)?.label} course.`,
          );
          return false;
        }

        const slotKey = `${sched.day}_${sched.timeSlot}`;
        if (scheduledSlots.has(slotKey)) {
          setError(
            `${child.name} cannot have multiple courses scheduled at the same time (${sched.day} at ${sched.timeSlot}). Please choose a different schedule.`,
          );
          return false;
        }
        scheduledSlots.add(slotKey);
      }
    }
    return true;
  };

  const handleCheckout = async () => {
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    let activeEnrolmentId = enrolmentId;

    try {
      // 1. Create or update Firestore pending enrolment doc(s) in students collection
      setStage("saving_enrolment");

      const batch = writeBatch(db);

      const updatedChildrenData = childrenData.map((c) => {
        let childId = c.id;
        if (!childId) {
          if (parentsDetails?.accountType === "student") {
            childId = parentId;
          } else {
            const newDocRef = doc(collection(db, "students"));
            childId = newDocRef.id;
          }
        }
        return { ...c, id: childId };
      });

      setChildrenData(updatedChildrenData);

      updatedChildrenData.forEach((c) => {
        const childDocRef = doc(db, "students", c.id);
        const childData = {
          name: c.name,
          school: c.school || "",
          grade: c.grade,
          parentUid:
            parentsDetails?.accountType === "student" ? null : parentId,
          enrolment: {
            ...c.enrolment,
            "ai-lab": {
              courses: c.interests.reduce((acc, courseId) => {
                acc[courseId] = c.schedule[courseId] || {};
                return acc;
              }, {}),
              paymentStatus: "pending",
              enrolledAt: Timestamp.fromDate(new Date()),
              otherQueries: otherQueries || "",
            },
          },
        };
        batch.set(childDocRef, childData, { merge: true });
      });

      await batch.commit();

      activeEnrolmentId = parentId;
      setEnrolmentId(activeEnrolmentId);

      // 2. Load payment script
      setStage("creating_order");
      try {
        await loadRazorpayScript();
      } catch (err) {
        setError(
          "Razorpay failed to load. Please disable ad-blockers and check your connection.",
        );
        setLoading(false);
        return;
      }

      // 3. Initiate Order through PHP API
      const backendUrl = `${envConfig.backendUrl}/paymentV1`;
      const currency = "INR";
      const keyRes = await axios.get(`${backendUrl}?action=getkey`, {
        timeout: 10000,
      });
      const key = keyRes.data.key;

      const orderRes = await axios.post(
        `${backendUrl}?action=checkout`,
        { amount: totalAmount, currency },
        { timeout: 12000 },
      );
      const order = orderRes.data.order;

      // 4. Razorpay options configuration
      const options = {
        key,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency,
        name: envConfig.appFullName,
        description: "AI Lab Enrolment",
        image: "/img/appLogo.png",
        prefill: {
          name: auth?.currentUser?.displayName || parentsDetails?.name || "",
          email: parentEmail || auth?.currentUser?.email || "",
          contact: parentContact || auth?.currentUser?.phoneNumber || "",
        },
        notes: {
          enrolmentId: activeEnrolmentId,
          uid: parentId || "",
        },
        theme: { color: "#2563EB" },
        handler: async (response) => {
          try {
            setStage("verifying");
            setLoading(true);

            // Backend payment verification check
            const verificationResponse = await axios.post(
              `${backendUrl}?action=paymentVerification`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                receipt: order?.receipt,
                amount: order?.amount,
                currency: order?.currency,
              },
              { timeout: 12000 },
            );

            if (verificationResponse?.data?.status === "success") {
              setStage("saving");

              const batch = writeBatch(db);

              const paymentRef = doc(
                db,
                "subscription",
                response.razorpay_payment_id,
              );
              batch.set(
                paymentRef,
                {
                  orderId: response.razorpay_order_id,
                  amount: order.amount,
                  currency: order.currency,
                  paymentId: response.razorpay_payment_id,
                  paymentSignature: response.razorpay_signature,
                  uid: parentId || "",
                  status: "verified",
                  paymentAt: Timestamp.fromDate(new Date()),
                  enrolmentId: activeEnrolmentId,
                  paymentFor: updatedChildrenData.map((c) => ({
                    childId: c.id,
                    program,
                    courses: c.interests,
                  })),
                },
                { merge: true },
              );

              updatedChildrenData.forEach((c) => {
                const childDocRef = doc(db, "students", c.id);
                batch.update(childDocRef, {
                  "enrolment.ai-lab.paymentStatus": "verified",
                  "enrolment.ai-lab.paymentRef": paymentRef.id,
                  "enrolment.ai-lab.paymentId": response.razorpay_payment_id,
                  "enrolment.ai-lab.paymentAt": Timestamp.fromDate(new Date()),
                });
              });

              await batch.commit();

              // Trigger WhatsApp Enrollment Success Notification
              try {
                const accountType =
                  parentsDetails?.accountType === "student"
                    ? "student"
                    : "parent";
                const isStudentMode = accountType === "student";
                const payload = {
                  accountType,
                  phone:
                    parentContact ||
                    parentsDetails?.phoneNumber ||
                    parentsDetails?.phone ||
                    auth?.currentUser?.phoneNumber ||
                    "",
                };

                if (isStudentMode) {
                  payload.studentName =
                    parentsDetails?.name ||
                    auth?.currentUser?.displayName ||
                    "Student";
                  payload.courses = updatedChildrenData[0].interests.map(
                    (courseId) => {
                      const courseInfo = AI_LAB_COURSES.find(
                        (c) => c.id === courseId,
                      );
                      const sched =
                        updatedChildrenData[0].schedule?.[courseId] || {};
                      return {
                        name: courseInfo ? courseInfo.label : courseId,
                        day: sched.day || "",
                        timeSlot: sched.timeSlot || "",
                      };
                    },
                  );
                } else {
                  payload.parentName =
                    parentsDetails?.name ||
                    auth?.currentUser?.displayName ||
                    "Parent";
                  payload.children = updatedChildrenData.map((c) => ({
                    name: c.name,
                    courses: c.interests.map((courseId) => {
                      const courseInfo = AI_LAB_COURSES.find(
                        (crs) => crs.id === courseId,
                      );
                      const sched = c.schedule?.[courseId] || {};
                      return {
                        name: courseInfo ? courseInfo.label : courseId,
                        day: sched.day || "",
                        timeSlot: sched.timeSlot || "",
                      };
                    }),
                  }));
                }

                await axios.post(
                  `${envConfig.backendUrl}/sendEnrollmentSuccessWebV1`,
                  payload,
                );
              } catch (e) {
                console.error(
                  "Failed to send WhatsApp enrolment success message:",
                  e,
                );
              }

              if (parentsDetails?.accountType === "student") {
                const updatedEnrolment = {
                  ...parentsDetails.enrolment,
                  "ai-lab": {
                    courses: updatedChildrenData[0].interests.reduce(
                      (acc, courseId) => {
                        acc[courseId] =
                          updatedChildrenData[0].schedule[courseId] || {};
                        return acc;
                      },
                      {},
                    ),
                    paymentStatus: "verified",
                    paymentRef: paymentRef.id,
                    paymentId: response.razorpay_payment_id,
                    paymentAt: new Date().toISOString(),
                  },
                };
                dispatch(updateUserDetails({ enrolment: updatedEnrolment }));
              }

              sessionStorage.removeItem("ai-lab-payment-retryData");
              setPaymentSuccess(true);
              setPaymentFailed(false);
            } else {
              throw new Error("Payment verification failed.");
            }
          } catch (err) {
            console.error("Verification error:", err);
            sessionStorage.setItem(
              "ai-lab-payment-retryData",
              JSON.stringify({
                response,
                order,
                activeEnrolmentId,
                childrenData: updatedChildrenData,
                accountType: parentsDetails?.accountType,
              }),
            );
            setError("Saved payment details failed. Please retry.");
            setPaymentFailed(true);
          } finally {
            setLoading(false);
            setStage("idle");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setStage("idle");
            setPaymentFailed(true);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setError("Transaction failed. Please check your bank status.");
        setLoading(false);
        setPaymentFailed(true);
      });

      setStage("opening_checkout");
      rzp.open();
    } catch (err) {
      console.error("Initiate order payment failed:", err);
      setError("Payment processing failed. Try again.");
      setLoading(false);
      setPaymentFailed(true);
    }
  };

  return {
    parentContact,
    parentEmail,
    validateForm,
    handleCheckout,
    loading,
    stage,
    error,
    setError,
  };
};

export default useEnrolmentCheckout;
